/**
 * Typed analytics API for Confluence.
 *
 * Use `useConfluenceAnalyticsEvents()` instead of calling `createAnalyticsEvent()` directly.
 * This hook enforces the correct Confluence analytics payload shape at compile time,
 * preventing the `eventType` vs `type` confusion that caused HOT-301294 (PIR-300717).
 *
 * ## IMPORTANT — two incompatible analytics schemas exist in this codebase:
 *
 * ❌ GAS3 / atlaskit components use:   { eventType: 'track', ... }
 * ✅ Confluence listener expects:       { type: 'sendTrackEvent', data: { ... } }
 *
 * Always use `useConfluenceAnalyticsEvents()` — it returns a `createEvent` function
 * that accepts a `ConfluenceAnalyticsPayload` and enforces the correct shape.
 *
 * @see AGENTS.md in this package for AI-agent guidance.
 */

import { useCallback } from 'react';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConfluenceAnalyticsEventType =
	| 'sendOperationalEvent'
	| 'sendPageEvent'
	| 'sendTrackEvent'
	| 'sendUIEvent'
	| 'sendScreenEvent';

/**
 * Strongly-typed payload for Confluence analytics events.
 * Pass this to `createEvent()` from `useConfluenceAnalyticsEvents()`.
 *
 * @example
 * const payload: ConfluenceAnalyticsPayload = {
 *   type: 'sendTrackEvent',
 *   data: {
 *     source: 'onboardingScreen',
 *     action: 'clicked',
 *     actionSubject: 'button',
 *   },
 * };
 */
export type ConfluenceAnalyticsPayload = {
	type: ConfluenceAnalyticsEventType;
	data: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook for firing Confluence analytics events with compile-time type safety.
 *
 * @example
 * const { createEvent } = useConfluenceAnalyticsEvents();
 *
 * const handleClick = () => {
 *   createEvent({
 *     type: 'sendTrackEvent',
 *     data: { source: 'onboardingScreen', action: 'clicked', actionSubject: 'button' },
 *   }).fire();
 * };
 */
export const useConfluenceAnalyticsEvents = (): {
	createEvent: (payload: ConfluenceAnalyticsPayload) => UIAnalyticsEvent;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const createEvent = useCallback(
		(payload: ConfluenceAnalyticsPayload): UIAnalyticsEvent => {
			return createAnalyticsEvent(payload);
		},
		[createAnalyticsEvent],
	);

	return { createEvent };
};
